import { Button } from "@/components/ui/button";
import { CheckCircle2, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Confirmation modal shown after a seller submits a custom card or sealed
 * product for admin review. Explains what happens next so sellers don't
 * re-create the same item (a common frustration when they only see a small
 * toast).
 *
 * Props:
 *   open       – boolean
 *   onClose    – fn
 *   kind       – "card" | "sealed" | "custom"
 *   quantity   – optional int (defaults to 1). Displayed in the copy so
 *                "4 productos sellados" reads naturally.
 */
export function ReviewSentModal({ open, onClose, kind = "card", quantity = 1 }) {
  const nav = useNavigate();
  if (!open) return null;
  const q = Math.max(1, parseInt(quantity, 10) || 1);
  const labels = {
    card:   { one: "carta",             many: "cartas" },
    custom: { one: "carta personalizada", many: "cartas personalizadas" },
    sealed: { one: "producto sellado",  many: "productos sellados" },
  }[kind] || { one: "artículo", many: "artículos" };
  const noun = q === 1 ? labels.one : labels.many;
  const article = q === 1
    ? (kind === "sealed" ? "Tu" : "Tu")
    : "Tus";
  const verb = q === 1 ? "está" : "están";
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]"
      data-testid="review-sent-modal"
      onClick={onClose}
    >
      <div
        className="bg-[#111] border border-emerald-500/40 rounded-md p-6 max-w-md w-full space-y-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
            <CheckCircle2 size={20} className="text-emerald-400" />
          </div>
          <div className="font-display text-2xl">¡Enviado a revisión!</div>
        </div>

        <div className="text-sm text-neutral-300 leading-relaxed space-y-3">
          <p>
            {article} <b className="text-white">{q > 1 ? `${q} ${noun}` : noun}</b> {verb} en la cola de un administrador. Te avisamos por correo cuando {q === 1 ? "esté aprobada" : "estén aprobadas"}.
          </p>
          <div className="p-3 bg-emerald-500/5 border border-emerald-500/30 rounded text-emerald-200 flex items-start gap-2">
            <Search size={16} className="mt-0.5 shrink-0" />
            <div>
              <b>Una vez aprobado</b>, {q === 1 ? "lo vas a" : "los vas a"} encontrar en el <b>buscador de publicar</b>. Ahí {q === 1 ? "lo publicas" : "los publicas"} con tu precio y fotos reales.
            </div>
          </div>
          <p className="text-amber-300 text-xs">
            💡 <b>No vuelvas a crearlo desde cero</b> — ya quedó registrado. Si lo creas de nuevo se generará un duplicado y el admin tendrá que rechazar uno.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 pt-2 border-t border-[#262626]">
          <Button
            data-testid="review-sent-goto-seller"
            onClick={() => { onClose(); nav("/seller?tab=catalog"); }}
            className="bg-[#EF4444] hover:bg-[#DC2626] flex-1 min-w-[140px]"
          >
            Ver Mi Catálogo
          </Button>
          <Button
            data-testid="review-sent-close"
            variant="outline"
            onClick={onClose}
            className="border-[#262626]"
          >
            Registrar {kind === "sealed" ? "otro sellado" : "otra"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ReviewSentModal;
